import { useEffect, useMemo, useRef, useState } from "react";
import { NEW_STUDIO_SYSTEM_PROMPT } from "@/api/ai/newStudioSystemPrompt";
import { scriptsApi } from "@/api/client";
import type {
  AiChatSidebarProps,
  StudioScript,
} from "@/components/ai/chat/AiChatSidebar";
import { useServices } from "@/hooks/useApi";
import {
  pipelineViewFromCode,
  studioLastError,
  testCasesFor,
  unreadScriptRows,
  unresolvedServicesIn,
  withoutSavedScripts,
  withSavedScript,
} from "./aiHelperContext";
import { buildPipelineCode, resolveCodeDraft } from "./pipelineCode";
import { usePipelineStaging } from "./pipelineStaging";
import { useStudioPipeline } from "./StudioPipelineProvider";
import {
  parseResourceId,
  parseSchemaRowId,
  type StagedResource,
  untitledScriptIds,
} from "./stagingModel";
import { buildStudioSnapshot } from "./studioSnapshot";
import { type InsertHost, useApplyAiCode } from "./useApplyAiCode";
import { useStudioState } from "./useStudioState";

// The new Studio's AI Helper feed: the sidebar keeps the old Studio's prop
// contract (AiChatSidebarProps → context items), this hook fills it from
// the staging state — stages, editor documents, the pipeline code, the
// last run and lint — and hands Insert to useApplyAiCode.

type Drafts = Record<string, string>;

// A saved script's content, fetched once per staged row and remembered;
// a row with an unsaved draft reads the draft instead, and a row the
// Studio saved itself reads the staging copy (TTTL-224) — nothing fetches
// back a file the Studio just wrote. A draft that goes away (saved or
// discarded) drops the remembered copy so the next read takes the saved
// text. The cache is state, replaced on every write: the memo downstream
// recomputes when a fetch lands, not only when the stage list changes
// again.
function useSavedScripts(
  transforms: StagedResource[],
  scriptDrafts: Drafts,
  savedScripts: Drafts,
): ReadonlyMap<string, string> {
  const [saved, setSaved] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );
  const draftedBefore = useRef(new Set<string>());
  const draftedKey = Object.keys(scriptDrafts).join("|");

  // biome-ignore lint/correctness/useExhaustiveDependencies: reacts to the set of drafted rows, not the draft texts
  useEffect(() => {
    const gone = [...draftedBefore.current].filter(
      (id) => scriptDrafts[id] == null,
    );
    draftedBefore.current = new Set(Object.keys(scriptDrafts));
    if (gone.length > 0) setSaved((prev) => withoutSavedScripts(prev, gone));
  }, [draftedKey]);

  const missing = unreadScriptRows(
    transforms.map((t) => t.id),
    scriptDrafts,
    savedScripts,
    saved,
  );
  const missingKey = missing.join("|");

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch only when the set of unread rows changes
  useEffect(() => {
    if (missingKey === "") return;
    let cancelled = false;
    for (const id of missing) {
      readScript(id).then((content) => {
        if (!cancelled && content !== null) {
          setSaved((prev) => withSavedScript(prev, id, content));
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [missingKey]);

  return saved;
}

// The saved text; "" when the read fails (the row is then not retried);
// null for an id that isn't a resource.
async function readScript(id: string): Promise<string | null> {
  const target = parseResourceId(id);
  if (!target) return null;
  try {
    const file = await scriptsApi.get(target.serviceName, target.resourceName);
    return file.content;
  } catch {
    return "";
  }
}

// The staged transforms' scripts in stage order; with nothing staged, the
// untitled editor drafts (the same rule as Run pipeline).
function useStagedScripts(
  transforms: StagedResource[],
  scriptDrafts: Drafts,
  savedScripts: Drafts,
): StudioScript[] {
  const saved = useSavedScripts(transforms, scriptDrafts, savedScripts);
  return useMemo(() => {
    if (transforms.length === 0) {
      return untitledScriptIds(scriptDrafts).map((id, i) => ({
        name: `Untitled script ${i + 1}.rhai`,
        content: scriptDrafts[id] ?? "",
      }));
    }
    return transforms.map((t) => ({
      name: t.name,
      content:
        scriptDrafts[t.id] ?? savedScripts[t.id] ?? saved.get(t.id) ?? "",
      serviceName: parseResourceId(t.id)?.serviceName,
    }));
  }, [transforms, scriptDrafts, savedScripts, saved]);
}

// The pipeline as the code editor shows it: the user's code draft, else
// the document generated from the stages.
export function usePipelineCode() {
  const s = usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  const generated = useMemo(
    () =>
      buildPipelineCode(
        {
          sources: s.sources,
          transforms: s.transforms,
          sinks: s.sinks,
          schemas: s.schemas,
        },
        s.pipelineExtras,
      ),
    [s.sources, s.transforms, s.sinks, s.schemas, s.pipelineExtras],
  );
  const code = resolveCodeDraft(s.pipelineCodeDraft, generated, editsLocked);
  return { generated, code };
}

// The source stage's collection/route, as the input document's origin.
function inputOriginOf(sources: StagedResource[]) {
  const first = sources[0] ? parseResourceId(sources[0].id) : null;
  return first
    ? { serviceName: first.serviceName, resource: first.resourceName }
    : null;
}

// The selected schema row's store entry and service ("schema:<service>:
// <id>", instance ids carry a "|slot" suffix) — what the chat's schema
// context item names.
function selectedSchema(
  selectedId: string | null,
): Pick<AiChatSidebarProps, "schemaId" | "schemaServiceName"> {
  if (!selectedId) return {};
  const owner = parseSchemaRowId(selectedId.split("|")[0]);
  if (!owner) return {};
  return { schemaId: owner.entryId, schemaServiceName: owner.serviceName };
}

type Staging = ReturnType<typeof usePipelineStaging>;

// What the open pipeline is called — workspace_info for the backend, which
// still speaks of workspaces; the prompt calls it the pipeline.
function pipelineIdentity(
  openPipeline: ReturnType<typeof useStudioPipeline>["openPipeline"],
  s: Staging,
): Pick<
  AiChatSidebarProps,
  | "workspaceId"
  | "workspaceName"
  | "linkedScripts"
  | "hasLinkedSchema"
  | "hasLinkedTestSuite"
  | "testSuiteId"
> {
  const suiteId = s.stagedSuite?.suiteId ?? null;
  return {
    workspaceId: openPipeline?.workspace.id ?? null,
    workspaceName: openPipeline?.workspace.name ?? null,
    linkedScripts: s.transforms.length,
    hasLinkedSchema: s.schemas.length > 0,
    hasLinkedTestSuite: suiteId !== null,
    testSuiteId: suiteId,
  };
}

// The editor documents and the last run, as the sidebar wants them.
function editorDocuments(
  s: Staging,
): Pick<
  AiChatSidebarProps,
  | "inputDocument"
  | "inputOrigin"
  | "outputDocument"
  | "schema"
  | "schemaId"
  | "schemaServiceName"
  | "testCases"
> {
  const run = s.scriptRunOutput;
  return {
    inputDocument: s.inputDocument,
    inputOrigin: inputOriginOf(s.sources),
    outputDocument: run?.success ? run.text : undefined,
    schema: s.schemaDocument,
    ...selectedSchema(s.effectiveSelectedId),
    testCases: testCasesFor(s.tests, s.testAssertions, run?.testRun),
  };
}

/** The sidebar's props plus the Insert host the panel wires to the
 *  chat (button labels) and the Replace confirm. */
export function useAiHelperProps(): {
  sidebar: AiChatSidebarProps;
  insert: InsertHost;
} {
  const s = usePipelineStaging();
  const { openPipeline, editsLocked, actionError } = useStudioPipeline();
  const { data: services } = useServices();
  const serviceNames = useMemo(
    () => (services ? new Set(services.map((x) => x.name)) : null),
    [services],
  );
  const scripts = useStagedScripts(
    s.transforms,
    s.scriptDrafts,
    s.savedScripts,
  );
  const { generated, code } = usePipelineCode();
  const pipelineView = useMemo(() => pipelineViewFromCode(code), [code]);
  const insert = useApplyAiCode(generated);
  // The explorer and workbench, appended to the prompt on every request.
  const studioState = useStudioState();
  const snapshot = useMemo(
    () => buildStudioSnapshot(studioState),
    [studioState],
  );

  const sidebar: AiChatSidebarProps = {
    scripts,
    ...editorDocuments(s),
    ...pipelineIdentity(openPipeline, s),
    lastError: studioLastError({
      actionError,
      run: s.scriptRunOutput,
      lintStatus: s.pipelineCodeLintStatus,
      codeIssues: s.codeIssues,
    }),
    pipelineView,
    unresolvedServices: unresolvedServicesIn(pipelineView, serviceNames),
    // A running deployment is view-only: answers, but no Insert.
    onApplyCode: editsLocked ? undefined : insert.apply,
    attachScriptsByDefault: true,
    // The helper sees what the view shows; the chips hide, not attach.
    contextMode: "follows-view",
    systemPrompt: `${NEW_STUDIO_SYSTEM_PROMPT}\n${snapshot}\n`,
  };
  return { sidebar, insert };
}
